import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, Dropdown, Input, Menu, Skeleton, Tag } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { Search } from 'lucide-react';
import { useLazyQuery } from '@apollo/client';
import InfiniteScroll from 'react-infinite-scroll-component';
import ActivityItem from '@src/components/activityItems/activityItems';
import { getActivityLogQuery } from '@src/util/query/dashboard.query';
import { RangeBadge, rangeVariables } from './widgetRange';

// number of activity log rows fetched per scroll page - keeps the initial
// load fast while still letting the user scroll through the full day
const ACTIVITY_LOG_PAGE_SIZE = 50;

// the dashboard's activity feed; `settings.range` is its period, kept with the widget
export const ActivityLogWidget = React.memo(({ widgetId, settings, onSettings }) => {
  const scrollId = `activity-log-scroll-${widgetId}`;
  const dateFilter = settings?.range || 'today';
  const rangeKey = `${dateFilter}:${settings?.from || ''}:${settings?.to || ''}`;
  const [activityActionFilter, setActivityActionFilter] = useState('all');
  const [activityEntityFilter, setActivityEntityFilter] = useState('all');
  const [activitySearchTags, setActivitySearchTags] = useState([]);
  const [activitySearchInput, setActivitySearchInput] = useState('');
  const [activitySearchVisible, setActivitySearchVisible] = useState(false);
  const activitySearchRef = useRef(null);

  const [getActivityLog, { loading: activityLogLoading }] = useLazyQuery(getActivityLogQuery);

  // accumulated rows across every page loaded so far for the current date filter
  const [allActivityLogs, setAllActivityLogs] = useState([]);
  const [activityLogHasMore, setActivityLogHasMore] = useState(true);
  // tracks which page the in-flight/most recent response belongs to, set
  // synchronously (ahead of state) so a stale/out-of-order response can be dropped
  const activityLogPageRef = useRef(1);

  // merges straight off the settled query result rather than a useEffect
  // watching the hook's `data` - Apollo's cache can hand back the exact same
  // `data` reference when a fresh response normalizes to something deeply
  // equal to what's already cached (e.g. flipping back to a filter you just
  // viewed), which would leave a useEffect([data]) never re-firing
  const fetchActivityLogPage = useCallback(async (page) => {
    const { startDate, endDate } = rangeVariables(settings);
    activityLogPageRef.current = page;
    const result = await getActivityLog({
      fetchPolicy: 'network-only',
      variables: {
        startDate,
        endDate,
        page,
        pageSize: ACTIVITY_LOG_PAGE_SIZE,
      }
    });

    const response = result?.data?.getActivityLog?.response;
    if (!response) return;
    if (activityLogPageRef.current !== page) return;

    const totalCount = result?.data?.getActivityLog?.totalCount ?? 0;
    setAllActivityLogs(prev => {
      const merged = page === 1 ? response : [...prev, ...response];
      setActivityLogHasMore(merged.length < totalCount);
      return merged;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeKey, getActivityLog]);

  // date filter changed (today/weekly/monthly/custom) - start over from page 1
  useEffect(() => {
    setAllActivityLogs([]);
    setActivityLogHasMore(true);
    fetchActivityLogPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeKey]);

  const loadMoreActivityLogs = useCallback(() => {
    if (activityLogLoading) return;
    fetchActivityLogPage(activityLogPageRef.current + 1);
  }, [activityLogLoading, fetchActivityLogPage]);

  useEffect(() => {
    if (activitySearchVisible) {
      setTimeout(() => activitySearchRef.current?.focus(), 50);
    }
  }, [activitySearchVisible]);

  const filteredActivityLogs = allActivityLogs.filter(d => {
    const actionMatch = activityActionFilter === 'all' || d?.action === activityActionFilter;
    const entityMatch = activityEntityFilter === 'all' || d?.entity?.trim() === activityEntityFilter;
    const searchMatch = activitySearchTags.length === 0 ||
      activitySearchTags.some(tag => (d?.target || '').toLowerCase().includes(tag.toLowerCase()));
    return actionMatch && entityMatch && searchMatch;
  });

  const activityEntities = [...new Set(allActivityLogs.map(d => d?.entity?.trim()))].filter(Boolean);

  const actionFilterMenu = (
    <Menu onClick={({ key }) => setActivityActionFilter(key)}>
      <Menu.Item key="all">All Actions</Menu.Item>
      <Menu.Item key="CREATE">Create</Menu.Item>
      <Menu.Item key="UPDATE">Update</Menu.Item>
      <Menu.Item key="DELETE">Delete</Menu.Item>
      <Menu.Item key="VIEW">View</Menu.Item>
    </Menu>
  );

  const entityFilterMenu = (
    <Menu onClick={({ key }) => setActivityEntityFilter(key)}>
      <Menu.Item key="all">All Entities</Menu.Item>
      {activityEntities.map(entity => (
        <Menu.Item key={entity}>{entity}</Menu.Item>
      ))}
    </Menu>
  );

  return (
    <Card
      className="activity-card dw-card"
      size="small"
      style={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius:'14px' }}
      bodyStyle={{ padding: 0, display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}
    >
      {/* ── fixed header + filters ── */}
      <div style={{ flexShrink: 0, padding: '12px 12px 8px', borderBottom: '1px solid #f0f0f0', background: '#fff', borderRadius:'14px' }}>
        <div className="chart-header">
          <span className="chart-title">Activity Logs</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span
              onClick={() => {
                setActivitySearchVisible(v => !v);
                if (activitySearchVisible) setActivitySearchInput('');
              }}
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', color: activitySearchVisible || activitySearchTags.length > 0 ? '#126E7D' : '#8c8c8c' }}
            >
              <Search size={14} />
            </span>
            <RangeBadge settings={settings} onChange={onSettings} />
          </div>
        </div>

        {activitySearchVisible && (
          <div style={{ marginTop: '8px' }}>
            <Input
              ref={activitySearchRef}
              placeholder="Type a name and press Enter..."
              value={activitySearchInput}
              size="small"
              onChange={e => setActivitySearchInput(e.target.value)}
              onPressEnter={() => {
                const val = activitySearchInput.trim();
                if (val && !activitySearchTags.includes(val)) {
                  setActivitySearchTags(prev => [...prev, val]);
                }
                setActivitySearchInput('');
              }}
              onKeyDown={e => {
                if (e.key === 'Escape') {
                  setActivitySearchVisible(false);
                  setActivitySearchInput('');
                }
              }}
              onBlur={() => {
                if (!activitySearchInput.trim()) setActivitySearchVisible(false);
              }}
            />
          </div>
        )}

        {activitySearchTags.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '8px' }}>
            {activitySearchTags.map(tag => (
              <Tag
                key={tag}
                closable
                onClose={() => setActivitySearchTags(prev => prev.filter(t => t !== tag))}
                style={{ borderRadius: '12px', fontSize: '11px' }}
                color="cyan"
              >
                {tag}
              </Tag>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
          <Dropdown overlay={actionFilterMenu} trigger={['click']}>
            <span className="metric-badge clickable">
              {activityActionFilter === 'all' ? 'All Actions' : activityActionFilter} <DownOutlined style={{ fontSize: '10px' }} />
            </span>
          </Dropdown>
          <Dropdown overlay={entityFilterMenu} trigger={['click']}>
            <span className="metric-badge clickable">
              {activityEntityFilter === 'all' ? 'All Entities' : activityEntityFilter} <DownOutlined style={{ fontSize: '10px' }} />
            </span>
          </Dropdown>
        </div>
      </div>

      {/* ── scrollable log list ── */}
      <div id={scrollId} style={{ flex: 1, overflow: 'auto', padding: '8px 12px 12px' }}>
        {activityLogLoading && allActivityLogs.length === 0 ? (
          <Skeleton avatar active paragraph={{ rows: 4 }} />
        ) : filteredActivityLogs.length === 0 ? (
          <div style={{ color: 'gray', textAlign: 'center', padding: '24px 0' }}>No activity logs found.</div>
        ) : (
          <InfiniteScroll
            dataLength={filteredActivityLogs.length}
            next={loadMoreActivityLogs}
            hasMore={activityLogHasMore}
            scrollableTarget={scrollId}
            loader={
              <div style={{ padding: '8px 0' }}>
                <Skeleton avatar active paragraph={{ rows: 2 }} />
              </div>
            }
            endMessage={
              <div style={{ color: '#bbb', textAlign: 'center', padding: '12px 0', fontSize: '12px' }}>
                No more logs
              </div>
            }
          >
            {filteredActivityLogs.map((d, index) => (
              <ActivityItem
                key={index}
                user={d?.target}
                action={d?.action}
                target={d?.recordLabel}
                location={d?.entity}
                time={d?.date}
                change={d?.changes}
              />
            ))}
          </InfiniteScroll>
        )}
      </div>
    </Card>
  );
});
